import { updateLogoChange } from '../webGL';
import {
  sanitizeScale,
  setLogoScale,
  getLogoScale,
  isValidScale,
  SCALE_CONSTANTS,
  scaleToSlider,
} from './scaleUtils';

export const findTextById = (textList, id) => textList.find((item) => item?.Id === id);

export const callWithTimeouts = (func, textId, delays = [25, 100, 300, 500, 1000]) => {
  const timeoutIds = [];
  delays.forEach((delay) => {
    timeoutIds.push(setTimeout(() => func(textId), delay));
  });
  return () => timeoutIds.forEach(clearTimeout);
};

export const forceCorrectTextOrientation = (textId, findTextById) => {
  if (!textId) return;
  const timeoutIds = [];

  try {
    const logo = findTextById(textId);
    if (!logo) return () => {};

    ['forceTextOrientation', 'correctTextOrientation', 'fixTextMirror', 'preventTextFlip'].forEach(
      (funcName) => {
        if (typeof window[funcName] === 'function') window[funcName](textId);
      },
    );

    if (typeof window.setTextRotation === 'function') {
      const currentRotation = logo.Rotate || 0;
      window.setTextRotation(textId, 0);
      timeoutIds.push(setTimeout(() => window.setTextRotation(textId, currentRotation), 50));
    }

    if (typeof window.resetTextTransform === 'function') {
      window.resetTextTransform(textId);
      timeoutIds.push(
        setTimeout(() => {
          if (logo.Position && typeof window.setTextPosition === 'function') {
            window.setTextPosition(textId, logo.Position.x, logo.Position.y, logo.Position.z);
          }
          if (typeof window.setTextRotation === 'function') {
            window.setTextRotation(textId, logo.Rotate);
          }
          if (typeof window.setTextScale === 'function') {
            window.setTextScale(textId, logo.Scale);
          }
        }, 100),
      );
    }
  } catch (error) {}

  return () => timeoutIds.forEach(clearTimeout);
};

export const syncTextState = (textId, findTextById) => {
  if (!textId) return;
  const logo = findTextById(textId);
  if (logo && logo.Position !== null) {
    try {
      updateLogoChange(logo);
      [
        { func: 'setTextScale', value: logo.Scale },
        { func: 'setTextRotation', value: logo.Rotate },
        { func: 'setTextColor', value: logo.Color },
        { func: 'setTextFont', value: logo.Font },
      ].forEach(({ func, value }) => {
        if (typeof window[func] === 'function') window[func](textId, value);
      });
    } catch (error) {}
  }
};

export const preserveTextScale = (textId, findTextById) => {
  if (!textId) return;
  const timeoutIds = [];

  try {
    const logo = findTextById(textId);
    if (!logo) return () => {};

    const actualScale = getLogoScale(logo);
    if (typeof window.setTextScale === 'function') {
      window.setTextScale(textId, actualScale);
      timeoutIds.push(
        setTimeout(() => {
          if (typeof window.getTextScale === 'function') {
            const currentScale = window.getTextScale(textId);
            if (currentScale !== actualScale) window.setTextScale(textId, actualScale);
          }
        }, 100),
      );
    }
  } catch (error) {}

  return () => timeoutIds.forEach(clearTimeout);
};

export const forceScaleSync = (textId, findTextById, setScale, setTextList) => {
  try {
    const logo = findTextById(textId);
    if (!logo) return;

    let actualScale = getLogoScale(logo);
    if (typeof window.getTextScale === 'function') {
      try {
        const sceneScale = window.getTextScale(textId);
        if (sceneScale && isValidScale(sceneScale)) {
          actualScale = sanitizeScale(sceneScale);
          setLogoScale(logo, actualScale);
        }
      } catch (error) {}
    }

    setScale(scaleToSlider(actualScale));
    if (typeof window.setTextScale === 'function') {
      window.setTextScale(textId, actualScale);
    }
  } catch (error) {}
};

export const fixHorizontalMirroring = (textId, findTextById) => {
  if (!textId) return;
  const timeoutIds = [];

  try {
    const logo = findTextById(textId);
    if (!logo) return () => {};

    if (typeof window.fixTextMirror === 'function') {
      window.fixTextMirror(textId);
    } else if (typeof window.correctTextMirror === 'function') {
      window.correctTextMirror(textId);
    } else if (typeof window.flipTextHorizontally === 'function') {
      window.flipTextHorizontally(textId);
    }

    if (typeof window.setTextScale === 'function') {
      const currentScale = getLogoScale(logo);
      if (typeof window.setTextScaleX === 'function') {
        window.setTextScaleX(textId, -currentScale);
        timeoutIds.push(setTimeout(() => window.setTextScaleX(textId, currentScale), 100));
      } else {
        window.setTextScale(textId, -currentScale);
        timeoutIds.push(setTimeout(() => window.setTextScale(textId, currentScale), 100));
      }
    }

    if (typeof window.setTextRotation === 'function') {
      const currentRotation = logo.Rotate || 0;
      window.setTextRotation(textId, currentRotation + 180);
      timeoutIds.push(setTimeout(() => window.setTextRotation(textId, currentRotation), 100));
    }
    if (typeof window.resetTextTransform === 'function') {
      window.resetTextTransform(textId);
      timeoutIds.push(
        setTimeout(() => {
          if (logo.Position && typeof window.setTextPosition === 'function') {
            window.setTextPosition(textId, logo.Position.x, logo.Position.y, logo.Position.z);
          }
          if (typeof window.setTextRotation === 'function')
            window.setTextRotation(textId, logo.Rotate);
          if (typeof window.setTextScale === 'function')
            window.setTextScale(textId, getLogoScale(logo));
        }, 150),
      );
    }
  } catch (error) {}

  return () => timeoutIds.forEach(clearTimeout);
};

export const intercept3DUpdates = (textList) => {
  try {
    if (typeof window.renderTextImage === 'function') {
      const originalRenderTextImage = window.renderTextImage;
      window.renderTextImage = function (...args) {
        const textId = args[3];
        const logo = textList.find((i) => i?.Id === textId);
        if (logo && logo._prevent3DUpdates) return;
        return originalRenderTextImage.apply(this, args);
      };
    }
    if (typeof window.updateLogoChange === 'function') {
      const originalUpdateLogoChange = window.updateLogoChange;
      window.updateLogoChange = function (logo) {
        if (logo && logo._prevent3DUpdates) return;
        return originalUpdateLogoChange.apply(this, arguments);
      };
    }
  } catch (error) {}
};

export const ultimateColorChange = (
  textId,
  newColor,
  textList,
  forceCorrectTextOrientation,
  fixHorizontalMirroring,
) => {
  if (!textId) return;
  const logo = textList.find((i) => i?.Id === textId);
  const cleanupFuncs = [];

  if (logo) {
    try {
      const currentState = {
        position: logo.Position,
        rotation: logo.Rotate,
        scale: getLogoScale(logo),
        isNew: logo.isNew,
      };
      logo.Color = newColor;
      logo._prevent3DUpdates = true;

      if (typeof window.updateTextColorOnly === 'function') {
        window.updateTextColorOnly(textId, newColor);
      } else if (typeof window.setTextColorOnly === 'function') {
        window.setTextColorOnly(textId, newColor);
      } else if (typeof window.updateTextMaterial === 'function') {
        window.updateTextMaterial(textId, newColor);
      }

      requestAnimationFrame(() => {
        if (currentState.position && typeof window.setTextPosition === 'function') {
          window.setTextPosition(
            textId,
            currentState.position.x,
            currentState.position.y,
            currentState.position.z,
          );
        }
        if (typeof window.setTextRotation === 'function') {
          window.setTextRotation(textId, currentState.rotation);
        }
        if (typeof window.setTextScale === 'function') {
          const actualScale = sanitizeScale(currentState.scale);
          window.setTextScale(textId, actualScale);
          const timeoutId = setTimeout(() => {
            if (typeof window.getTextScale === 'function') {
              const currentScale = window.getTextScale(textId);
              if (currentScale !== actualScale) window.setTextScale(textId, actualScale);
            }
          }, 50);
          cleanupFuncs.push(() => clearTimeout(timeoutId));
        }
        const cleanupOrientation = forceCorrectTextOrientation(textId);
        if (cleanupOrientation) cleanupFuncs.push(cleanupOrientation);
        const cleanupMirroring = fixHorizontalMirroring(textId);
        if (cleanupMirroring) cleanupFuncs.push(cleanupMirroring);
      });
      const timeoutId = setTimeout(() => {
        logo._prevent3DUpdates = false;
      }, 100);
      cleanupFuncs.push(() => clearTimeout(timeoutId));

      if (textId && typeof window.renderTextImage === 'function') {
        window.renderTextImage(logo.Text, logo.Color, logo.Font, textId);
      }

      return () => cleanupFuncs.forEach((fn) => fn && fn());
    } catch (error) {
      return () => {};
    }
  }
  return () => {};
};

export const conservativeTextUpdate = (
  logo,
  property,
  value,
  activeTextId,
  forceCorrectTextOrientation,
) => {
  if (!logo || !activeTextId) return;
  const timeoutIds = [];

  try {
    const currentState = {
      position: logo.Position,
      rotation: logo.Rotate,
      scale: getLogoScale(logo),
      isNew: logo.isNew,
    };

    if (property === 'Scale') {
      const sanitizedScale = sanitizeScale(value);
      if (sanitizedScale !== value) value = sanitizedScale;
      currentState.scale = sanitizedScale;
    }

    logo[property] = value;

    if (property === 'Color') {
      if (typeof window.updateTextColor === 'function') {
        window.updateTextColor(activeTextId, value);
      } else if (typeof window.setTextColor === 'function') {
        window.setTextColor(activeTextId, value);
      }
    } else if (property === 'Font') {
      if (typeof window.updateTextFont === 'function') {
        window.updateTextFont(activeTextId, value);
      } else if (typeof window.setTextFont === 'function') {
        window.setTextFont(activeTextId, value);
      }
    } else if (property === 'Scale') {
      if (typeof window.updateTextScale === 'function') {
        window.updateTextScale(activeTextId, value);
      } else if (typeof window.setTextScale === 'function') {
        window.setTextScale(activeTextId, value);
      }
    } else if (property === 'Rotate') {
      if (typeof window.updateTextRotation === 'function') {
        window.updateTextRotation(activeTextId, value);
      } else if (typeof window.setTextRotation === 'function') {
        window.setTextRotation(activeTextId, value);
      }
    }

    timeoutIds.push(
      setTimeout(() => {
        if (currentState.position && typeof window.setTextPosition === 'function') {
          window.setTextPosition(
            activeTextId,
            currentState.position.x,
            currentState.position.y,
            currentState.position.z,
          );
        }
        if (typeof window.setTextRotation === 'function') {
          window.setTextRotation(activeTextId, currentState.rotation);
        }
        if (typeof window.setTextScale === 'function') {
          let actualScale = getLogoScale({ Scale: currentState.scale }) || SCALE_CONSTANTS.DEFAULT;
          if (!isValidScale(actualScale)) {
            actualScale = SCALE_CONSTANTS.DEFAULT;
            if (logo) setLogoScale(logo, actualScale);
          }
          window.setTextScale(activeTextId, actualScale);
          timeoutIds.push(
            setTimeout(() => {
              if (typeof window.getTextScale === 'function') {
                const currentScale = window.getTextScale(activeTextId);
                if (currentScale !== actualScale) window.setTextScale(activeTextId, actualScale);
              }
            }, 50),
          );
        }

        const cleanupOrientation = forceCorrectTextOrientation(activeTextId);
        if (cleanupOrientation) timeoutIds.push(cleanupOrientation);

        updateLogoChange(logo);
        if (activeTextId && typeof window.renderTextImage === 'function') {
          window.renderTextImage(logo.Text, logo.Color, logo.Font, activeTextId);
        }
      }, 50),
    );

    return () =>
      timeoutIds.forEach((id) => {
        if (typeof id === 'function') id();
        else clearTimeout(id);
      });
  } catch (error) {
    return () => {};
  }
};
